<script setup>
import { computed, ref, watch } from 'vue'

// A color as `#rrggbb`: typed into the text input, taken from the browser's color picker in front
// of it, or one of the `presets` beneath. Only the text input is submitted; `inputAttrs` are the
// attributes simple_form gave it (see app/inputs/color_input.rb).
const props = defineProps({
  value: { type: String, default: '' },
  presets: { type: Array, default: () => [] },
  inputAttrs: { type: Object, default: () => ({}) },
})

const FORMAT = /^#[0-9a-f]{6}$/i

const color = ref(props.value)

// The picker only takes a color, so while the text isn't one it keeps showing the last that was
const shown = ref(FORMAT.test(props.value) ? props.value : (props.presets[0] ?? '#000000'))
watch(color, (value) => {
  if (FORMAT.test(value)) shown.value = value
})
// and only in lower case
const picked = computed(() => shown.value.toLowerCase())

function selected(preset) {
  return preset.toLowerCase() === color.value.toLowerCase()
}
</script>

<template>
  <div class="color-input">
    <div class="input-group">
      <input
        type="color"
        class="form-control form-control-color"
        :value="picked"
        :disabled="inputAttrs.disabled"
        :aria-label="$t('color_input.picker')"
        @input="color = $event.target.value">
      <input
        type="text"
        maxlength="7"
        pattern="#[0-9A-Fa-f]{6}"
        spellcheck="false"
        autocomplete="off"
        v-bind="inputAttrs"
        v-model="color">
    </div>
    <div class="form-text">{{ $t('color_input.hint') }}</div>
    <div class="color-input-presets" role="group" :aria-label="$t('color_input.presets')">
      <button
        v-for="preset in presets"
        :key="preset"
        type="button"
        class="color-input-preset"
        :style="{ backgroundColor: preset }"
        :title="preset"
        :aria-label="preset"
        :aria-pressed="selected(preset)"
        :disabled="inputAttrs.disabled"
        @click="color = preset"></button>
    </div>
  </div>
</template>
